import React, { Component, PropTypes } from 'react';
import { scroller } from 'react-scroll';
import pick from 'lodash/pick';
import classnames from 'classnames';
import { Field, reduxForm } from 'redux-form';
import { StarRatingField, BasicTextAreaField, CheckBoxField } from 'common/components/form_fields';
import {
  FIELDS, INITIAL_VALUES, shouldShowPublicReview
} from 'project/components/wrapup/survey_fields';
import Money from 'common/components/money';
import AnimatedButton from 'common/components/animated_button';
import PostFeedback from 'project/containers/wrapup/post_feedback';

/**
 * Survey wrapup step.
 *
 * @author Nathan Ozelim <nathan@startupfoundry.com>
 */

let Form = ({error, handleSubmit, submitting, submitFailed, ratedUsername, isCreative,
             showCsSurvey, curValues, pristine}) => {
  const sections = [
    { name: 'user', title: `Rate ${ratedUsername}`, show: true },
    { name: 'cs', title: `Rate crowdspring`, show: showCsSurvey }
  ];

  return (
    <form onSubmit={handleSubmit}>
      {sections.filter(s => s.show).map((section, i) => (
      <div key={i} className={classnames("survey-section", { "with-border": i > 0 })}>
        <h3>{ section.title }</h3>
        <fieldset>
          <div className="row">
            <div className="small-16 medium-12 columns">
              {FIELDS.map((field, j) => {
                const shouldShow = (
                  field.section == section.name && field.shouldShow(curValues, isCreative)
                )

                if (!shouldShow) {
                  return null
                }

                const Component = {
                  rating: StarRatingField,
                  text: BasicTextAreaField,
                  checkbox: CheckBoxField
                }[field.type]

                return (
                  <Field key={j} name={field.name} label={field.label} component={Component}
                    textualType={field.textualType}
                    description={field.description(ratedUsername, isCreative)}/>)
              })}
            </div>
          </div>
        </fieldset>
      </div>))}

      <AnimatedButton processing={submitting} type="submit" label="Submit my feedback"
        processingLabel="Submitting..." errorMsg={error || submitFailed}
        successMsg="Done!" disabled={pristine} baseBtnClass="btn btn-tertiary btn-block" />
  </form>)
}

Form = reduxForm({
  form: "WrapupSurvey",
})(Form);


export default class Survey extends Component {
  constructor(props) {
    super(props);

    this.handleSubmit = this.handleSubmit.bind(this);
  }

  handleSubmit(data) {
    const {
      wrapup: { award, creative, project_id }, showCsSurvey, submitUserSurveyRequest,
      submitCsSurveyRequest, createTestimonialRequest, fetchWrapupsIfNeeded, fetchProject,
      isCreative
    } = this.props;

    const userSurveyData = pick(
      data, FIELDS.filter(s => s.section == 'user').map(s => s.name)
    );

    const csSurveyData = pick(
      data, FIELDS.filter(s => s.section == 'cs').map(s => s.name)
    );
    /* `additional_comments` field show up twice, so we need to use a different name in the form */
    csSurveyData['additional_comments'] = csSurveyData['cs_additional_comments'];

    const testimonialData = {
      rating: data['overall_satisfaction'],
      comment: data['public_review']
    };

    /* Steps:
     * 1) submit CS survey (optional)
     * 2) submit creative testimonial (optional)
     * 3) submit user survey
    */

    /* submit CS survey if that's the case */
    const step1 = (
      showCsSurvey? submitCsSurveyRequest(award.id, {...csSurveyData}) : Promise.resolve()
    );

    return step1.finally(() => {

      /* submit creative testimonial if that's the case */
      const step2 = (shouldShowPublicReview(data, isCreative)?
        createTestimonialRequest(award.id, testimonialData):
        Promise.resolve()
      );
      return step2;

    }).finally(() => {

      /* submit user survey */
      return submitUserSurveyRequest(award.id, userSurveyData);

    }).finally(() => {
      /* reload wrap-ups and project, and scroll to top of page */
      fetchWrapupsIfNeeded(project_id, true);
      fetchProject(project_id);
      scroller.scrollTo('app-header', { containerId: 'app-scroller' });
    });
  }

  render() {
    const {
      wrapup: {
        creative, client, client_survey_submitted_at, creative_survey_submitted_at,
        cs_survey_submitted_at, creative_testimonial_submitted_at,
        award
      },
      values, showCsSurvey, isCreative, alreadySubmitted, isAdmin
    } = this.props;
    const ratedUsername = isCreative? client.username : creative.username;

    return (
      <div className="wrapup-step survey">
        { alreadySubmitted || isAdmin?
        <PostFeedback {...this.props} />:
        <div>
          <div className="row">
            <div className="small-16 large-14 columns">
              <header className="step-header">
                <h3>
                  Feedback for Award { award.position }
                  <span className="award-value"> (<Money value={award.value} />)</span>
                </h3>
                { isCreative?
                <div>
                  <p>
                    Mission accomplished! Take your victory lap and raise a glass. You've completed
                    your project, and may we say, we love your work! Thanks for being part of our
                    team.
                  </p>
                  <p>
                    Now it's your turn to rate the client. Please take a moment to assign a star
                    value (5 stars for best; 1 star for terrible) to each of three criteria:
                    timing, communication and your overall satisfaction.
                  </p>
                </div> :
                <p>
                  Your ratings and comments are important to { creative.username }'s reputation,
                  so please take a moment to assign a star value (5 stars for best; 1 star for
                  terrible) to each of the three criteria: timing, communication and your overall
                  satisfaction with their work in this project.
                  { showCsSurvey && <span> After you
                  rate { creative.username }, we'll ask you a few questions about your experience
                  with crowdspring and our service.</span> }
                </p> }
              </header>
            </div>
          </div>
          <Form onSubmit={this.handleSubmit} initialValues={INITIAL_VALUES} curValues={values}
            ratedUsername={ratedUsername} isCreative={isCreative} showCsSurvey={showCsSurvey}/>
        </div> }
      </div>
    )
  }
}

Survey.propTypes = {
  wrapup: PropTypes.object.isRequired,
  details: PropTypes.object.isRequired,
  values: PropTypes.object,
  showCsSurvey: PropTypes.bool.isRequired,
  submitUserSurveyRequest: PropTypes.func.isRequired,
  submitCsSurveyRequest: PropTypes.func.isRequired,
  createTestimonialRequest: PropTypes.func.isRequired,
  fetchWrapupsIfNeeded: PropTypes.func.isRequired,
  fetchProject: PropTypes.func.isRequired,
  isCreative: PropTypes.bool.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  alreadySubmitted: PropTypes.bool.isRequired,
};



// WEBPACK FOOTER //
// ./project/components/wrapup/survey.jsx